import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AccountSelector from "../components/AccountSelector";
import { FaPlus, FaChartLine, FaListAlt, FaTrash, FaPencilAlt, FaTable, FaEye, FaEyeSlash, FaCalculator } from "react-icons/fa";
import { collection, getDocs, query, orderBy, doc, updateDoc, onSnapshot, getDoc, where, limit } from "firebase/firestore";
import { db } from "../firebase";
import { deleteAccount, migrateUserHistoricalTrades, getUserSettings, updateUserSettings } from "../services/FirestoreService";
import { useAuth } from "../contexts/GoogleAuthContext";
import { setDoc } from "firebase/firestore"; // Ensure setDoc is imported
import { FaLightbulb } from "react-icons/fa"; // Add FaLightbulb to imports
import SimpleCalculator from '../components/SimpleCalculator'; 
import EndOfTrialModal from "../components/EndOfTrialModal";


 
const API_URL = process.env.REACT_APP_API_URL || "https://trapoundapi.onrender.com";

// --- Robust “Backtesting” detector across roots & settings ---
const isBacktesting = (acc) => {
  const s = acc?.settings || {};
  const rootTag = String(acc?.tag || "").toLowerCase();
  const rootFlag = acc?.isBacktest === true;

  const setTag = String(s?.tag || "").toLowerCase();
  const setFlag = s?.isBacktest === true;

  const rootFunding = String(acc?.fundingType || "").toLowerCase();
  const setFunding  = String(s?.fundingType || "").toLowerCase();

  return (
    rootFlag || setFlag ||
    rootTag.includes("backtest") || setTag.includes("backtest") ||
    rootFunding.includes("backtest") || setFunding.includes("backtest")
  );
};

const deriveCopyRole = (a) => {
  const ct = a?.settings?.CopyTrade || a?.CopyTrade || null;
  const enabled = typeof ct?.enabled === "boolean" ? ct.enabled : true;
  const roleLower = String(ct?.role ?? "").trim().toLowerCase();
  if (enabled === false) return null;
  if (roleLower === "leader") return "leader";
  if (roleLower === "follower") return "follower";

  const candidates = [
    a?.copyTradingTag, a?.copyTraderTag, a?.copyRole, a?.copyTradingRole, a?.role,
    a?.settings?.copyTradingTag, a?.settings?.copyTraderTag, a?.settings?.copyRole, a?.settings?.copyTradingRole, a?.settings?.role,
    a?.settings?.tag, a?.tag,
  ];
  for (const c of candidates) {
    const s = String(c ?? "").trim().toLowerCase();
    if (!s) continue;
    if (/\bleader\b/.test(s)) return "leader";
    if (/\bfollower\b/.test(s)) return "follower";
  }
  return null;
};

// --- Helper: Parse numbers safely ---
function parseNum(v) {
  if (v == null) return NaN;
  if (typeof v === "number") return v;
  const n = Number(String(v).replace(/[$,]/g, ""));
  return Number.isFinite(n) ? n : NaN;
}

// --- Helper: Get Balance ---
function getBalanceFromAccount(account) {
  if (!account) return 0;
  const settings = account.settings || {};

  // 1. Prioritize the dynamic Running Balance first
  const runningBalance = 
    settings["Running Balance"] ?? 
    settings["Running balance"] ?? 
    settings.runningBalance ?? 
    settings.running_balance ?? 
    account["Running Balance"] ?? 
    account["Running balance"] ?? 
    account.runningBalance ?? 
    account.running_balance ?? 
    account.balance;

  // Safely check for numbers, including exactly 0
  if (runningBalance !== undefined && runningBalance !== null && runningBalance !== "") {
      return Number(runningBalance);
  }

  // 2. Fallback to Total Equity / Beginning Balance if no trades have updated the running balance yet
  const totalEquity = settings["Total Equity"] ?? settings["Beginning Balance"];
  
  if (totalEquity !== undefined && totalEquity !== null && totalEquity !== "") {
      return Number(totalEquity);
  }

  return 0;
}

// --- Helpers for Insight Logic ---

// Force a date string to be parsed as LOCAL time, avoiding UTC shifts
const parseDateLocal = (dateInput) => {
  if (!dateInput) return null;
  
  // If it's already a Firestore Timestamp, convert it
  if (dateInput.toDate) return dateInput.toDate();
  
  // If it's a JS Date, return it
  if (dateInput instanceof Date) return dateInput;

  // If it's a string, handle the formats
  const str = String(dateInput).trim();
  
  // Handle "YYYY-MM-DD" manually to prevent UTC shift
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d); // Local midnight
  }

  // Handle "MM/DD/YYYY" (Standard JS behavior is usually local, but let's be safe)
  if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(str)) {
     return new Date(str); 
  }

  // Fallback
  return new Date(str);
};

// Check if two dates represent the same calendar day
const isSameDay = (d1, d2) => {
  const date1 = parseDateLocal(d1);
  const date2 = parseDateLocal(d2);
  
  if (!date1 || !date2 || isNaN(date1) || isNaN(date2)) return false;
  
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
};

// --- Helpers for Insight Logic ---

// 1. Convert any input (Timestamp, Date, String) to "YYYY-MM-DD"
const toISODateString = (input) => {
  if (!input) return null;

  // A. Handle Firestore Timestamp
  if (input.toDate) input = input.toDate();

  // B. Handle "YYYY-MM-DD" String (The user's specific format)
  if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    return input; // Already perfect, don't touch it or timezone will mess it up
  }

  // C. Handle "MM/DD/YYYY" String (just in case)
  if (typeof input === "string" && input.includes("/")) {
    const d = new Date(input);
    if (isNaN(d.getTime())) return null;
    input = d;
  }

  // D. Handle Standard Date Object (Extract Local YYYY-MM-DD)
  if (input instanceof Date) {
    const year = input.getFullYear();
    const month = String(input.getMonth() + 1).padStart(2, '0');
    const day = String(input.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  return null;
};

// 2. Get "Yesterday" as a "YYYY-MM-DD" string
const getPreviousTradingDayString = () => {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  let daysToSubtract = 1;

  if (day === 1) daysToSubtract = 3; // Mon -> Fri
  else if (day === 0) daysToSubtract = 2; // Sun -> Fri
  
  // Create the date object
  const prevDate = new Date(now);
  prevDate.setDate(now.getDate() - daysToSubtract);
  
  // Return simple string format
  return toISODateString(prevDate);
};

// --- Helper: Parse R:R ---
function parseRiskToReward(value) {
  if (value == null) return null;
  const str = String(value).trim();
  const colon = str.match(/^\s*(-?\d*\.?\d+)\s*:\s*(-?\d*\.?\d+)\s*$/);
  if (colon) {
    const a = parseFloat(colon[1]);
    const b = parseFloat(colon[2]);
    const isOne = (x) => Number.isFinite(x) && Math.abs(Math.abs(x) - 1) < 1e-9;
    if (Number.isFinite(a) && Number.isFinite(b) && a !== 0 && b !== 0) {
      if (isOne(a) && !isOne(b)) return b / Math.abs(a);
      if (isOne(b) && !isOne(a)) return a / Math.abs(b);
      return b / Math.abs(a);
    }
    return null;
  }
  const single = str.match(/-?\d*\.?\d+/);
  if (single) {
    const val = parseFloat(single[0]);
    return Number.isFinite(val) ? val : null;
  }
  return null;
}

// --- Helper: Format R:R for display ---
function formatRewardToRisk(ratio) {
  if (ratio == null || !isFinite(ratio)) return "--";
  const sign = ratio < 0 ? "-" : "";
  const abs = Math.abs(ratio);
  const left = parseFloat(abs.toFixed(2)).toString();
  return `${sign}${left}:1`;
}

function getProductShort(val) {
  return val ? val.split(" - ")[0] : "";
}

function isFundingRow(row) {
  const type = String(row?.["Funding Type"] || "").toLowerCase();
  return row?.isFunding === true
    || type === "deposit"
    || type === "withdrawal"
    || getProductShort(row?.["Product"]) === "Funding";
}

function getCurrentDayOfYear() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now - start + ((start.getTimezoneOffset() - now.getTimezoneOffset()) * 60 * 1000);
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

// --- Robust Date Checker (Fixes Timezone Issues) ---
function isDateToday(dateVal) {
    if (!dateVal) return false;
    const today = new Date();

    // 1. Handle Firestore Timestamp objects
    if (dateVal.toDate) dateVal = dateVal.toDate();

    // 2. Handle "YYYY-MM-DD" strings manually
    if (typeof dateVal === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateVal)) {
        const [y, m, d] = dateVal.split('-').map(Number);
        return y === today.getFullYear() &&
               (m - 1) === today.getMonth() &&
               d === today.getDate();
    }

    // 3. Handle standard Date objects or other string formats
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return false;

    return d.getDate() === today.getDate() &&
           d.getMonth() === today.getMonth() &&
           d.getFullYear() === today.getFullYear();
}

function isDateThisWeek(dateVal) {
    if (!dateVal) return false;
    
    const today = new Date();
    const tzOffset = today.getTimezoneOffset() * 60000;
    const localNow = new Date(today - tzOffset);
    
    let dObj;
    if (dateVal.toDate) {
        dObj = dateVal.toDate();
    } else if (typeof dateVal === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateVal)) {
        const [y, m, d] = dateVal.split('-').map(Number);
        dObj = new Date(y, m - 1, d);
    } else {
        dObj = new Date(dateVal);
    }

    if (isNaN(dObj.getTime())) return false;

    // Reset times for accurate day comparison
    dObj.setHours(0, 0, 0, 0);
    localNow.setHours(0, 0, 0, 0);

    // Calculate Sunday to Saturday week bounds
    const startOfWeek = new Date(localNow);
    startOfWeek.setDate(localNow.getDate() - localNow.getDay()); 
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    return dObj >= startOfWeek && dObj <= endOfWeek;
}



const getPreviousTradingDayRange = () => {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  let daysToSubtract = 1;

  if (day === 1) daysToSubtract = 3; // Mon -> Fri
  else if (day === 0) daysToSubtract = 2; // Sun -> Fri
  else if (day === 6) daysToSubtract = 1; // Sat -> Fri (or just ignore Sat)

  const prevDate = new Date(now);
  prevDate.setDate(now.getDate() - daysToSubtract);
  prevDate.setHours(0, 0, 0, 0);

  const prevEnd = new Date(prevDate);
  prevEnd.setHours(23, 59, 59, 999);

  return { start: prevDate, end: prevEnd, dateString: prevDate.toISOString().split('T')[0] };
};

// --- Components: Modals ---

function EditAccountModal({ open, onClose, onSave, currentName, loading }) {
  const [newName, setNewName] = useState(currentName);

  useEffect(() => {
    setNewName(currentName);
  }, [currentName]);

  if (!open) return null;

  const handleSave = () => {
    if (newName.trim() && newName.trim() !== currentName) {
      onSave(newName.trim());
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl px-8 py-8 min-w-[340px] max-w-[95vw] border-2 border-blue-400">
        <div className="mb-4">
          <FaPencilAlt className="text-3xl text-blue-500 mx-auto mb-2" />
          <h2 className="text-xl font-bold text-blue-700 dark:text-blue-400 mb-4">Edit Account Name</h2>
          <label className="block text-left text-sm font-medium text-gray-600 dark:text-gray-300 mb-2" htmlFor="accountName">
            Account Name
          </label>
          <input
            id="accountName"
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="flex justify-center gap-4 mt-6">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2 rounded-lg bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-bold hover:bg-gray-300 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold transition"
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDeleteModal({ open, onClose, onConfirm, accountName, loading }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl px-8 py-8 min-w-[340px] max-w-[95vw] border-2 border-red-400 text-center">
        <div className="mb-4">
          <FaTrash className="text-3xl text-red-500 mx-auto mb-2" />
          <h2 className="text-xl font-bold text-red-700 dark:text-red-400 mb-2">Delete Account</h2>
          <div className="text-gray-700 dark:text-gray-300">
            Are you sure you want to delete <b>{accountName}</b>?<br />
            This action cannot be undone.
          </div>
        </div>
        <div className="flex justify-center gap-4 mt-6">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-6 py-2 rounded-lg bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-200 font-bold hover:bg-gray-300 transition"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-6 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-bold transition"
          >
            {loading ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function WhatsNewModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl px-8 py-8 w-full max-w-lg mx-4 border-2 border-blue-400">
        <h2 className="text-2xl font-bold text-blue-700 dark:text-blue-400 mb-4">What's New!</h2>
        <div className="text-gray-700 dark:text-gray-300 space-y-4 mb-6 leading-relaxed">
          <p>
            🚀 <strong>New Features: Strategy Checklist</strong>
          </p>
          <p>
            You can now view your strategy checklist on the <span className="text-blue-400"> Entry forms</span>. Verify your strategy steps and check them off to ensure everything lines up before entering the trade.
          </p>
        </div>
        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold transition"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
}




function StrategyReminderModal({ open, onClose, onNavigate }) {
  // 1. Add a state to track if the buttons are clickable
  const [isClickable, setIsClickable] = useState(false);

  // 2. Add a 500ms delay before allowing clicks when the modal opens
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => setIsClickable(true), 500);
      return () => clearTimeout(timer);
    } else {
      setIsClickable(false); // Reset when closed
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 sm:p-10 min-w-[340px] max-w-lg border-2 border-blue-400">
        <h2 className="text-2xl font-bold mb-4 text-center text-blue-700 dark:text-blue-400">
          Protect Your Capital
        </h2>
        
        <div className="mb-8 text-gray-700 dark:text-gray-300 text-sm space-y-5 text-left leading-relaxed">
          <p>
            Before you take your next trade, we highly recommend defining your <strong>Trading Rules</strong>, <strong>Recovery Strategy</strong>, and <strong>Loss Strategy</strong> while you have a clear mind.
          </p>

          <div>
            <p>
              Your <strong><span className="text-blue-400">Trading Rules</span></strong> are your core requirements for entering and managing a trade. 
            </p>
            <p className="text-gray-500 dark:text-gray-400 italic mt-1">
              Example: "I will only trade the NY session, wait for a candle close, and always use a stop loss."
            </p>
          </div>
          
          <div>
            <p>
              A <strong><span className="text-blue-400">Recovery Strategy</span></strong> are the exact steps you will take to regain composure and protect your account after a losing day. 
            </p>
            <p className="text-gray-500 dark:text-gray-400 italic mt-1">
              Example: "I will stop trading, close my charts, and go for a walk before reviewing my journal."
            </p>
          </div>
          
          <div>
            <p>
              A <strong><span className="text-blue-400">Loss Strategy</span></strong> is your absolute hard rule for stepping away after your defined number of losing trades.
            </p>
            <p className="text-gray-500 dark:text-gray-400 italic mt-1">
              Example: "If I lose 3 trades in a row, I am strictly done trading for the day."
            </p>
          </div>

          <div>
           <p><strong><span className="text-blue-400">Knowing your plan</span></strong> is the key to <strong><span className="text-blue-400">long-term</span></strong> consistency. </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 mt-4">
          <button
            type="button"
            onClick={() => {
              if (isClickable) onNavigate();
            }}
            disabled={!isClickable}
            className={`font-bold px-8 py-3 rounded-xl shadow transition w-full ${
              isClickable 
                ? "bg-blue-600 hover:bg-blue-700 text-white" 
                : "bg-blue-400 text-white/70 cursor-not-allowed"
            }`}
          >
            Create Strategies Now
          </button>
          <button
            type="button"
            onClick={() => {
              if (isClickable) onClose();
            }}
            disabled={!isClickable}
            autoFocus // Steals default browser focus away from the primary button
            className={`font-semibold py-2 transition w-full ${
              isClickable
                ? "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                : "text-gray-400 cursor-not-allowed"
            }`}
          >
            Remind Me Later
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Main Dashboard ---
export default function Dashboard({
  accounts,
  selectedAccountId,
  setSelectedAccountId,
  onAddAccount,
  isAdmin
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [liveAccounts, setLiveAccounts] = useState(accounts);
  const selectedAccount = liveAccounts.find(a => a.id === selectedAccountId);
  // --- Edge Alert States ---
const [edgeAlert, setEdgeAlert] = useState(null);
const [loadingEdge, setLoadingEdge] = useState(false);

  // Quote states
  const [, setQuotes] = useState([]);
  const [quoteOfTheDay, setQuoteOfTheDay] = useState(null);

  // Modal states
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Account stats
  const [balance, setBalance] = useState(0);
  const [winRate, setWinRate] = useState(0);
  const [avgRewardToRisk, setAvgRewardToRisk] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);

  // --- Daily Summary States ---
    const [showDailySummary, setShowDailySummary] = useState(false);
    const [dailyStats, setDailyStats] = useState([]);
    const [loadingDaily, setLoadingDaily] = useState(false);
    const [isSGTSubscriber, setIsSGTSubscriber] = useState(false);

    // --- ADD THESE NEW VARIABLES HERE ---
    const [userPrefs, setUserPrefs] = useState({});
    const [prefsLoaded, setPrefsLoaded] = useState(false);
    const isPrivacyMode = userPrefs?.["Privacy Mode"] === true;

    // CHANGED: Initialize from localStorage so the setting persists
    const [hideValues, setHideValues] = useState(() => {
      const saved = localStorage.getItem("dashboardHideValues");
      return saved === "true";
    });

    // This overrides the manual toggle if Privacy Mode is strictly ON
    const effectiveHideValues = isPrivacyMode || hideValues;

    // --- New State for Insight ---
    const [dailyInsight, setDailyInsight] = useState(null);
    const [loadingInsight, setLoadingInsight] = useState(false);
    const [showEndOfTrial, setShowEndOfTrial] = useState(false); // [!code ++]
    const [trialEndDate, setTrialEndDate] = useState(null);
    const [showWhatsNew, setShowWhatsNew] = useState(false);
    const WHATS_NEW_VERSION_KEY = "hasSeenWhatsNew_v7";
    const [showAdminTotals, setShowAdminTotals] = useState(false);

    // --- Strategy Reminder State ---
  const [showStrategyReminder, setShowStrategyReminder] = useState(false);

useEffect(() => {
  if (!user?.uid || !prefsLoaded) return;

  // 1. Check if they have AT LEAST ONE strategy defined
const hasStrategies = userPrefs.tradingRules || userPrefs.recoveryStrategy || userPrefs.lossStrategy;
  
  if (hasStrategies) {
    setShowStrategyReminder(false);
    return;
  }

  const creationTime = new Date(user.metadata?.creationTime).getTime();
  const isNewUser = (Date.now() - creationTime) < (2 * 24 * 60 * 60 * 1000); 
  if (isNewUser) return;

  // Use Firebase preferences instead of localStorage
  const showCount = userPrefs.strategy_popup_count || 0;
  const lastShownDate = userPrefs.strategy_popup_last || "";
  const todayStr = new Date().toDateString();

  if (showCount < 3 && lastShownDate !== todayStr) {
    setShowStrategyReminder(true);
  }
}, [user, userPrefs, prefsLoaded]);



// ADD THE FUNCTION RIGHT HERE
const handleCloseStrategyReminder = async () => {
  setShowStrategyReminder(false);
  
  if (user?.uid) {
    const currentCount = userPrefs.strategy_popup_count || 0;
    
    try {
      // Save the count to Firestore so it syncs across all devices
      await updateUserSettings(user.uid, { 
        strategy_popup_count: currentCount + 1,
        strategy_popup_last: new Date().toDateString()
      });
    } catch (error) {
      console.error("Error updating strategy reminder preference:", error);
    }
  }
};


useEffect(() => {
    if (!user?.uid) return;

    const checkModalPreferences = async () => {
      try {
        const prefs = await getUserSettings(user.uid) || {};
        
        // Check if the user still needs to see the welcome video
        const needsWelcomeVideo = !prefs.hasSeenWelcomeVideo; 
        
        // Check if they've seen this specific version of the What's New module
        const hasSeenWhatsNew = !!prefs[WHATS_NEW_VERSION_KEY];

        // Only show What's New if they DON'T need the Welcome video AND haven't seen it yet
        if (!needsWelcomeVideo && !hasSeenWhatsNew) {
          setShowWhatsNew(true);
        }
      } catch (error) {
        console.error("Error checking modal preferences:", error);
      }
    };

    checkModalPreferences();
  }, [user?.uid]);

  // Handler to close the modal and update Firestore so they don't see it again
  const handleCloseWhatsNew = async () => {
    setShowWhatsNew(false);
    if (user?.uid) {
      try {
        await updateUserSettings(user.uid, { [WHATS_NEW_VERSION_KEY]: true });
      } catch (error) {
        console.error("Error updating What's New preference:", error);
      }
    }
  };


  // [!code ++] DEBUG VERSION: Check Trial Status
useEffect(() => {
    if (!user) return;

    const checkTrial = async () => {
      // [!code ++] 1. CHECK LOCAL STORAGE FIRST
      // If the user has already closed the modal, stop here.
      const storageKey = `trapound_trial_seen_${user.uid}`;
      if (localStorage.getItem(storageKey) === "true") {
        return; 
      }

      try {
        const res = await fetch(`${API_URL}/check-trial-status/${user.uid}`);
        
        if (res.ok) {
          const { status, trialEnd } = await res.json();
          
          if (trialEnd) {
            setTrialEndDate(new Date(trialEnd));
            const now = new Date();
            
            // Check if status is 'TRIALING' and date has passed
            const isTrialExpired = (status === 'TRIALING' || status === 'trialing') && now >= new Date(trialEnd);

            if (isTrialExpired) {
               setShowEndOfTrial(true);
            }
          }
        }
      } catch (error) {
        console.warn("Failed to check trial status via API:", error);
      }
    };

    checkTrial();
  }, [user]);

// --- Effect: Listen for User Preferences (Privacy Mode) ---
useEffect(() => {
  if (!user?.uid) return;
  const prefsRef = doc(db, "users", user.uid, "settings", "prefs");
  const unsubPrefs = onSnapshot(prefsRef, (docSnap) => {
    if (docSnap.exists()) {
      setUserPrefs(docSnap.data());
    }
    setPrefsLoaded(true); // Signal that Firebase has finished fetching
  });
  return () => unsubPrefs();
}, [user?.uid]);


// --- Effect: Daily Insight Logic (Morning Review Mode) ---
useEffect(() => {
  if (!user?.uid || !selectedAccount?.id) return;

const checkAndGenerateInsight = async () => {
    // 1. Define Today's Date String
    const todayStr = toISODateString(new Date());
    const insightRef = doc(db, "users", user.uid, "accounts", selectedAccount.id, "daily_insights", "current_insight");

    try {
      setLoadingInsight(true);

      // --- NEW: FAST EMPTY ACCOUNT CHECK ---
      // Do a highly efficient 1-document read to see if any trades exist at all
      const journalRef = collection(db, "users", user.uid, "accounts", selectedAccount.id, "journal");
      const singleTradeSnap = await getDocs(query(journalRef, limit(1)));

      if (singleTradeSnap.empty) {
          const isFirstAccount = liveAccounts.length <= 1;
          const welcomeInsight = {
              type: "welcome", 
              headline: isFirstAccount 
                  ? "Welcome to Your Trading Journey" 
                  : "Your Blank Slate Awaits",
              // --- UPDATED MESSAGING HERE ---
              content: isFirstAccount
                  ? "Your dashboard is ready! Once you start logging trades, this space will provide AI-driven insights and personalized wisdom based on your previous day's trading session."
                  : "This new account is ready to go. Once you start logging trades here, you'll receive customized AI insights analyzing your previous day's trading session.",
              // ------------------------------
              action: "Navigate to your Journal and log your first trade.",
              date: todayStr,
              generatedAt: Date.now(),
              analyzedDate: null
          };
          
          // Force overwrite the "stuck" AI cache if it exists
          const snap = await getDoc(insightRef);
          if (!snap.exists() || snap.data().type !== "welcome") {
              await setDoc(insightRef, welcomeInsight);
          }
          
          setDailyInsight(welcomeInsight);
          setLoadingInsight(false);
          return; // Stop execution here
      }
      // -------------------------------------

      // 2. FOR ACTIVE ACCOUNTS: Check if we already generated today's insight
      const snap = await getDoc(insightRef);
      if (snap.exists()) {
        const existingData = snap.data();
        if (existingData.date === todayStr) {
          setDailyInsight(existingData);
          setLoadingInsight(false);
          return; // Stop here if we've already generated an insight for today
        }
      }

      // 3. IF NO CACHE FOR TODAY: Fetch up to 50 recent trades
      const recentSnap = await getDocs(query(journalRef, limit(50))); 
      
      let allTrades = [];
      recentSnap.forEach(doc => {
          const data = doc.data();
          const d = data["Date"] || data["Entry Date"];
          const dateObj = parseDateLocal(d);
          
          if (dateObj) {
              allTrades.push({
                  rawDate: d,
                  dateStr: toISODateString(dateObj),
                  timestamp: dateObj.getTime(),
                  data: data
              });
          }
      });

      // 4. FILTER: Exclude Today's trades (Wait until tomorrow to analyze them)
      const historicTrades = allTrades.filter(t => t.dateStr !== todayStr);

      // 5. Sort Descending
      historicTrades.sort((a, b) => b.timestamp - a.timestamp);


      let mode = "wisdom"; 
      let tradesForAnalysis = [];
      let dateToAnalyze = null;

      // 6. Find most recent session
      if (historicTrades.length > 0) {
           const lastSessionTrade = historicTrades[0];
           dateToAnalyze = lastSessionTrade.dateStr; 
           
           const prevTradingDayStr = getPreviousTradingDayString();

           // Force re-analysis if the last session is from the "Previous Trading Day"
           const isMorningReview = dateToAnalyze === prevTradingDayStr;
           let shouldAnalyze = isMorningReview; 

           if (!isMorningReview) {
               // If it's not the immediate previous day, check if this specific date was ever analyzed
               // Note: This check still works across the collection even if we overwrite "current_insight"
               const insightsColRef = collection(db, "users", user.uid, "accounts", selectedAccount.id, "daily_insights");
               const qCheck = query(insightsColRef, where("analyzedDate", "==", dateToAnalyze), limit(1));
               const checkSnap = await getDocs(qCheck);
               shouldAnalyze = checkSnap.empty;
           }

           if (shouldAnalyze) {
               mode = "analysis";
               tradesForAnalysis = historicTrades
                  .filter(t => t.dateStr === dateToAnalyze)
                  .map(t => {
                      let rawTime = t.data["Entry Time"] || t.data["Time"];
                      if (rawTime && typeof rawTime === 'object') {
                           rawTime = rawTime.seconds ? new Date(rawTime.seconds * 1000).toLocaleTimeString() : String(rawTime);
                      }
                      return {
                          product: t.data.Product,
                          win: t.data.Win,
                          pnl: t.data["Actual Reward"],
                          mistake: t.data.Mistake || t.data.Notes || "", 
                          time: rawTime || "Unknown"
                      };
                  });
           } else {
               mode = "wisdom";
           }
      }

      // 7. Call API
      const API_URL = process.env.REACT_APP_API_URL || "https://trapoundapi.onrender.com";
      const res = await fetch(`${API_URL}/generateDailyInsight`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
              recentTrades: tradesForAnalysis, 
              mode 
          })
      });

      const json = await res.json();

      if (json.insight) {
          const newInsight = { 
              ...json.insight, 
              date: todayStr, // Saved inside the doc to prevent double-refreshing today
              generatedAt: Date.now(),
              analyzedDate: mode === "analysis" ? dateToAnalyze : null 
          };
          
          // Overwrites "current_insight" with the new data
          await setDoc(insightRef, newInsight);
          setDailyInsight(newInsight);
      }

    } catch (err) {
      console.error("Error generating daily insight:", err);
    } finally {
      setLoadingInsight(false);
    }
  };

  checkAndGenerateInsight();
}, [user?.uid, selectedAccount?.id, liveAccounts.length]);


// --- Effect: Calculate Statistical Edge ---
useEffect(() => {
  async function calculateEdge() {
    if (!user?.uid || !selectedAccount?.id) return;
    setLoadingEdge(true);
    
    try {
      const journalRef = collection(db, "users", user.uid, "accounts", selectedAccount.id, "journal");
      const snap = await getDocs(journalRef);
      const rows = snap.docs.map(doc => doc.data());

      if (rows.length < 15) {
         setEdgeAlert({
           title: "Edge Discovery",
           message: "Log at least 15 trades to discover your statistical trading edge."
         });
         setLoadingEdge(false);
         return;
      }

      const dayStats = {};
      const productStats = {};
      let totalWins = 0;
      let validTradeCount = 0;
      
      // 1. Gather all the data in one pass
      rows.forEach(row => {
         if (isFundingRow(row)) return;
         
         const pnl = parseNum(row["Actual Reward"]);
         const isWin = row.Win === true || row.Win === "TRUE";
         const dStr = row["Entry Date"] || row["Date"] || row["Exit Date"];
         const dObj = parseDateLocal(dStr);
         const product = getProductShort(row["Product"]);
         
         if (Number.isFinite(pnl)) {
             validTradeCount++;
             if (isWin) totalWins++;

             // Track Day of Week
             if (dObj) {
                 const dayName = dObj.toLocaleDateString('en-US', { weekday: 'long' });
                 if (!dayStats[dayName]) dayStats[dayName] = { pnl: 0, count: 0, wins: 0 };
                 dayStats[dayName].pnl += pnl;
                 dayStats[dayName].count += 1;
                 if (isWin) dayStats[dayName].wins += 1;
             }

             // Track Product/Asset
             if (product) {
                 if (!productStats[product]) productStats[product] = { pnl: 0, count: 0, wins: 0 };
                 productStats[product].pnl += pnl;
                 productStats[product].count += 1;
                 if (isWin) productStats[product].wins += 1;
             }
         }
      });

      const possibleEdges = [];

      // 2. Determine Best Day Edge
      let bestDay = null;
      let maxDayPnl = -Infinity;
      Object.keys(dayStats).forEach(day => {
          if (dayStats[day].count >= 3 && dayStats[day].pnl > maxDayPnl) {
              maxDayPnl = dayStats[day].pnl;
              bestDay = day;
          }
      });
      if (bestDay && maxDayPnl > 0) {
          const winRate = Math.round((dayStats[bestDay].wins / dayStats[bestDay].count) * 100);
          let msg = effectiveHideValues 
            ? `You perform best on ${bestDay}s with a ${winRate}% win rate. Look for your A+ setups.`
            : `You perform best on ${bestDay}s, generating ${Number(maxDayPnl).toLocaleString('en-US', { style: 'currency', currency: 'USD' })} with a ${winRate}% win rate.`;
          possibleEdges.push({ title: "Best Day to Trade", message: msg });
      }

      // 3. Determine Best Asset/Product Edge
      let bestProduct = null;
      let maxProdPnl = -Infinity;
      Object.keys(productStats).forEach(p => {
          if (productStats[p].count >= 3 && productStats[p].pnl > maxProdPnl) {
              maxProdPnl = productStats[p].pnl;
              bestProduct = p;
          }
      });
      if (bestProduct && maxProdPnl > 0) {
          const winRate = Math.round((productStats[bestProduct].wins / productStats[bestProduct].count) * 100);
          let msg = effectiveHideValues
            ? `${bestProduct} is your most profitable asset with a ${winRate}% win rate. Stick to what works.`
            : `${bestProduct} is your most profitable asset, generating ${Number(maxProdPnl).toLocaleString('en-US', { style: 'currency', currency: 'USD' })} with a ${winRate}% win rate.`;
          possibleEdges.push({ title: "Top Performing Asset", message: msg });
      }

      // 4. Overall Win Rate / Consistency Edge
      if (validTradeCount >= 15) {
          const overallWinRate = Math.round((totalWins / validTradeCount) * 100);
          if (overallWinRate >= 45) {
              possibleEdges.push({ 
                  title: "Consistent Edge", 
                  message: `Your overall win rate is sitting strong at ${overallWinRate}%. Trust your system and strictly manage your risk today.`
              });
          }
      }

      // 5. Rotate the insight based on the current day of the year
      if (possibleEdges.length > 0) {
          const dayOfYear = getCurrentDayOfYear();
          const selectedEdge = possibleEdges[dayOfYear % possibleEdges.length];
          setEdgeAlert(selectedEdge);
      } else {
          setEdgeAlert(null); // Hide if they have data but no profitable edges yet
      }

    } catch (err) {
       console.error("Failed to calculate edge:", err);
       setEdgeAlert(null);
    } finally {
      setLoadingEdge(false);
    }
  }
  
  calculateEdge();
}, [user?.uid, selectedAccount?.id, effectiveHideValues]);

    // NEW: Save to localStorage whenever the toggle changes
    useEffect(() => {
      localStorage.setItem("dashboardHideValues", hideValues);
    }, [hideValues]);

  // --- Effect: Get Running Balance for Selected ---
  useEffect(() => {
    setBalance(getBalanceFromAccount(selectedAccount));
  }, [selectedAccount]);

  // --- Effect: Live subscribe balance updates ---
  useEffect(() => {
    if (!user || !selectedAccount?.id) return;
    const accountRef = doc(db, "users", user.uid, "accounts", selectedAccount.id);
    const unsubscribe = onSnapshot(accountRef, (snap) => {
      const data = snap.data();
      if (data) {
        const accountData = { ...data, settings: data.settings || {} };
        setBalance(getBalanceFromAccount(accountData));
        setCopyRole(deriveCopyRole(accountData));
      }
    });
    return () => unsubscribe();
  }, [user, selectedAccount?.id]);

  // --- Effect: Calculate Win Rate & Avg R:R for Selected ---
  useEffect(() => {
    async function fetchWinAndRR() {
      if (!user?.uid || !selectedAccount?.id) {
        setWinRate(0);
        setAvgRewardToRisk(null);
        return;
      }
      const journalRef = collection(db,"users",user.uid,"accounts",selectedAccount.id,"journal");
      const snap = await getDocs(journalRef);
      const rows = snap.docs.map(doc => doc.data());
      if (!rows.length) {
        setWinRate(0);
        setAvgRewardToRisk(null);
        return;
      }
      // Win rate
      const wins = rows.filter(r => r.Win === true || r.Win === "TRUE").length;
      setWinRate(Math.round((wins / rows.length) * 100));

      // Avg Reward:Risk
      let sum = 0, n = 0;
      rows.forEach((row) => {
        if (isFundingRow(row)) return; 
        const rr = parseRiskToReward(row["Actual Risk to Reward"] ?? row["Risk/Reward"]);
        const pnl = Number(row["Actual Reward"]);
        if (rr == null || !isFinite(rr)) return;
        if (!Number.isFinite(pnl) || pnl === 0) return; 
        sum += pnl < 0 ? -Math.abs(rr) : Math.abs(rr);
        n += 1;
      });
      setAvgRewardToRisk(n ? (sum / n) : null);
    }
    fetchWinAndRR();
  }, [user?.uid, selectedAccount?.id]);

  // --- Effect: Fetch Quotes ---
  useEffect(() => {
    async function fetchQuotes() {
      try {
        const q = query(collection(db, "quotes"), orderBy("index"));
        const snap = await getDocs(q);
        const allQuotes = snap.docs.map(doc => doc.data());
        setQuotes(allQuotes);
        const dayNum = getCurrentDayOfYear();
        if (allQuotes.length > 0) {
          setQuoteOfTheDay(allQuotes[dayNum % allQuotes.length]);
        } else {
          setQuoteOfTheDay(null);
        }
      } catch (err) {
        setQuoteOfTheDay(null);
      }
    }
    fetchQuotes();
  }, []);

  // --- Effect: Sync Accounts ---
  useEffect(() => {
  if (!user) return;
  const q = collection(db, "users", user.uid, "accounts");
  const unsubscribe = onSnapshot(q, (snap) => {
    const updated = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((acc) => Boolean(acc.name && String(acc.name).trim())); // <-- Ignore zombie/unnamed docs
    setLiveAccounts(updated);
  });
  return () => unsubscribe();
}, [user]);

  // --- Effect: Check User Settings for Summary Card visibility ---
  useEffect(() => {
    async function checkSettings() {
        if(!user) return;
        try {
            const userDocRef = doc(db, "users", user.uid);
            const userSnap = await getDoc(userDocRef);
            if (userSnap.exists()) {
                const data = userSnap.data();
                setShowDailySummary(data.showDailySummary === true);
            }
        } catch (e) {
            console.error("Error fetching user settings", e);
        }
    }
    checkSettings();
  }, [user]);



// Effect: resolve SGT from 2 locations (User Root & Settings)
// REMOVED: Direct Subscriber collection queries to prevent permission errors
useEffect(() => {
  if (!user?.uid) return;

  let isMounted = true;
  
  // We track multiple sources. If ANY is true, the user is an SGT subscriber.
  let isUserRootSgt = false;      // Checks root user doc
  let isSettingsSgt = false;      // Checks ANY doc in users/{uid}/settings/

  const updateFinalState = () => {
    if (isMounted) setIsSGTSubscriber(isUserRootSgt || isSettingsSgt);
  };

  const toBool = (v) => v === true || v === 1 || (typeof v === "string" && v.toLowerCase() === "true");

  // ------------------------------------------------------
  // CHECK 1: User Root Document (users/{uid})
  // ------------------------------------------------------
  const userRef = doc(db, "users", user.uid);
  const unsubUser = onSnapshot(userRef, (snap) => {
    const data = snap.data() || {};
    const val1 = data.SGT ?? data.sgt;
    const val2 = data.settings?.SGT ?? data.settings?.sgt;
    isUserRootSgt = toBool(val1) || toBool(val2);
    updateFinalState();
  }, (err) => console.warn("User root check failed", err));

  // ------------------------------------------------------
  // CHECK 2: Settings Subcollection (users/{uid}/settings/*)
  // ------------------------------------------------------
  const settingsColRef = collection(db, "users", user.uid, "settings");
  const unsubSettings = onSnapshot(settingsColRef, (snap) => {
      let foundSgt = false;
      snap.docs.forEach(doc => {
          const d = doc.data();
          if (toBool(d.SGT) || toBool(d.sgt)) {
              foundSgt = true;
          }
      });
      isSettingsSgt = foundSgt;
      updateFinalState();
  }, (err) => {
      console.warn("Settings subcollection check failed", err);
      isSettingsSgt = false; 
  });

  return () => {
    isMounted = false;
    unsubUser();
    unsubSettings();
  };
}, [user?.uid]);


// --- Effect: Calculate Daily Stats for ALL Accounts ---
  useEffect(() => {
    if (!user || !showDailySummary || liveAccounts.length === 0) return;

    async function fetchDailyStats() {
        setLoadingDaily(true);
        const stats = [];

        const truthy = (v) => v === true || v === 1 || (typeof v === "string" && v.toLowerCase() === "true");

        for (const acc of liveAccounts) {
            const role = deriveCopyRole(acc);
            const settings = acc.settings || {};
            
            // 1. Get the standard Running Balance
            const currentBalance = getBalanceFromAccount(acc);
            
            // 2. Determine Effective Balance based on Account Type
            let effectiveBalance = currentBalance;
            
            const isPropFunded = truthy(settings.isPropFunded) || truthy(acc.isPropFunded) || String(acc.tag || settings.tag || "").toLowerCase() === "prop funded";
            const isPropEval = !isPropFunded && (truthy(settings.isPropFirm) || truthy(settings.isProp) || truthy(acc.isPropFirm) || truthy(acc.isProp));

            if (isPropFunded || isPropEval) {
                // Use the exact value stored in Firebase by the Prop Calculators
                const savedPropBalance = settings["Prop Running Balance"];
                
                if (savedPropBalance !== undefined && savedPropBalance !== null && savedPropBalance !== "") {
                    effectiveBalance = Number(savedPropBalance);
                } else {
                    // Safe Fallback: Only triggers if an account is brand new and hasn't been saved in the calculator yet
                    const maxLoss = Number(settings["Maximum Loss Limit"] || settings["Max Loss Limit"] || 0);
                    if (isPropFunded) {
                        effectiveBalance = Math.max(0, currentBalance + Math.abs(maxLoss));
                    } else {
                        const initial = Number(settings["Initial Balance"] || 0);
                        const blownAt = (initial > 0 && maxLoss > 0) ? Math.max(maxLoss, initial - maxLoss) : 0;
                        effectiveBalance = Math.max(0, currentBalance - blownAt);
                    }
                }
            } else {
                // Retail Logic: Use Beginning Balance/Total Equity if available
                const rawBeginning = settings["Beginning Balance"] ?? settings["Total Equity"];
                if (rawBeginning !== undefined && rawBeginning !== null && rawBeginning !== "") {
                    effectiveBalance = Number(rawBeginning);
                }
            }

            let dailyPnL = 0;
            let weeklyPnL = 0;
            let dailyRRSum = 0;
            let dailyRRCount = 0;

            try {
                const journalRef = collection(db, "users", user.uid, "accounts", acc.id, "journal");
                const snap = await getDocs(journalRef);
                
                snap.forEach(doc => {
                        const row = doc.data();
                        if (isFundingRow(row)) return;

                        const rowDate = row["Entry Date"] || row["Date"] || row["Exit Date"];
                        const pnl = parseNum(row["Actual Reward"]);

                        // Your existing daily check
                        if (isDateToday(rowDate)) {
                            if (Number.isFinite(pnl)) dailyPnL += pnl;
                            
                            const rr = parseRiskToReward(row["Actual Risk to Reward"] ?? row["Risk/Reward"]);
                            if (Number.isFinite(rr) && Number.isFinite(pnl) && pnl !== 0) {
                                 dailyRRSum += pnl < 0 ? -Math.abs(rr) : Math.abs(rr);
                                 dailyRRCount++;
                            }
                        }

                        // ADD THIS WEEKLY CHECK
                        if (isDateThisWeek(rowDate)) {
                            if (Number.isFinite(pnl)) weeklyPnL += pnl;
                        }
                    });

                // Calculate Percent Increase using Effective Balance
                const startBalance = effectiveBalance - dailyPnL;
                let percentChange = 0;
                
                if (Math.abs(startBalance) < 0.01) {
                    if (dailyPnL !== 0) {
                        percentChange = dailyPnL > 0 ? 100 : -100;
                    }
                } else {
                    percentChange = (dailyPnL / Math.abs(startBalance)) * 100;
                }

                const avgRR = dailyRRCount > 0 ? dailyRRSum / dailyRRCount : null;

                stats.push({
                  name: acc.name,
                  role: role,
                  type: acc.type,
                  balance: currentBalance,             
                  effectiveBalance: effectiveBalance,  
                  dailyPnL,
                  weeklyPnL,
                  percentChange,
                  avgRR
                });

            } catch (e) {
                console.error(`Error fetching stats for ${acc.name}`, e);
            }
        }

  

        stats.sort((a, b) => {
          const roleRank = (r) => (r === "leader" ? 0 : r === "follower" ? 1 : 2);

          const ra = roleRank(a.role);
          const rb = roleRank(b.role);
          if (ra !== rb) return ra - rb;

          const nameA = String(a?.name ?? "").trim();
          const nameB = String(b?.name ?? "").trim();

          // 2) Follower accounts (by name)
          if (ra === 1) {
            return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
          }

          // (Optional) If multiple leaders exist, keep them alphabetical
          if (ra === 0) {
            return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
          }

          // 3) Account type
          const typeA = String(a?.type ?? "").trim();
          const typeB = String(b?.type ?? "").trim();
          const typeCmp = typeA.localeCompare(typeB, undefined, { sensitivity: "base" });
          if (typeCmp !== 0) return typeCmp;

          // 4) Account Name
          return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
        });
        setDailyStats(stats);
        setLoadingDaily(false);
    }

    fetchDailyStats();
  }, [user, liveAccounts, showDailySummary]);


  // --- Logic: Badge Helpers ---
  const truthy = (v) => v === true || v === 1 || (typeof v === "string" && v.toLowerCase() === "true");

  const isPropEval =
     truthy(selectedAccount?.settings?.isPropFirm) ||
     truthy(selectedAccount?.settings?.isProp) ||
     truthy(selectedAccount?.isPropFirm) ||
     truthy(selectedAccount?.isProp);

  const tagValue =
      (selectedAccount?.tag && String(selectedAccount.tag)) ||
      (selectedAccount?.settings?.tag && String(selectedAccount.settings.tag)) ||
      "";

  const isPropFunded =
     truthy(selectedAccount?.settings?.isPropFunded) ||
     truthy(selectedAccount?.isPropFunded) ||
     tagValue.toLowerCase() === "prop funded";



  const typeBadge = (type, isPropEval, isPropFunded, isBacktest) => {
      const base = "inline-block px-2 py-1 rounded-full text-xs font-semibold mr-2";
      const isFutures = /^futures/i.test(String(type || ""));
      const isStocks  = /^stocks/i.test(String(type || ""));
      const isMixed   = /^mixed/i.test(String(type || ""));
      const isOptions = /^options/i.test(String(type || "")); // <-- ADD THIS

      let suffix = "Retail";
      if (isBacktest) suffix = "Backtesting";
      else if (isPropFunded) suffix = "Prop Funded";
      else if (isPropEval) suffix = "Prop Eval";
      
      const label = `${type} — ${suffix}`;
      
      return (
        <span
          className={`${base} ${
            isOptions
              ? "bg-orange-500 text-white" // <-- ADD THIS
              : isStocks
              ? "bg-purple-600 text-white"
              : isFutures
                ? "bg-blue-700 text-blue-300"
                : isMixed 
                  ? "bg-yellow-500 text-white"
                  : "bg-green-700 text-green-300"
          }`}
        >
          {label}
        </span>
      );
  };

  const deriveCopyRole = (a) => {
      const ct = a?.settings?.CopyTrade || a?.CopyTrade || null;
      const enabled = typeof ct?.enabled === "boolean" ? ct.enabled : true;
      const roleLower = String(ct?.role ?? "").trim().toLowerCase();
      if (enabled === false) return null;
      if (roleLower === "leader") return "leader";
      if (roleLower === "follower") return "follower";

      const candidates = [
        a?.copyTradingTag, a?.copyTraderTag, a?.copyRole, a?.copyTradingRole, a?.role,
        a?.settings?.copyTradingTag, a?.settings?.copyTraderTag, a?.settings?.copyRole, a?.settings?.copyTradingRole, a?.settings?.role,
        a?.settings?.tag, a?.tag,
      ];
      for (const c of candidates) {
        const s = String(c ?? "").trim().toLowerCase();
        if (!s) continue;
        if (/\bleader\b/.test(s)) return "leader";
        if (/\bfollower\b/.test(s)) return "follower";
      }
      return null;
  };

  const [copyRole, setCopyRole] = useState(deriveCopyRole(selectedAccount));
  useEffect(() => {
    setCopyRole(deriveCopyRole(selectedAccount));
  }, [selectedAccount]);

  const copyBadge = (role) => {
      if (!role) return null;
      const base = "inline-block px-2 py-1 rounded-full text-xs font-semibold mr-2";
      const isLeader = role === "leader";
      const label = isLeader ? "Leader" : "Follower";
      const colorClass = isLeader
        ? "bg-yellow-500 text-gray-100 dark:bg-yellow-300/90 dark:text-gray-700"
        : "bg-purple-100 text-purple-700 dark:bg-purple-700/40 dark:text-purple-300";

      return (
        <span className={`${base} ${colorClass}`}>
         {label}
        </span>
      );
  };

  const accountsForSelector = useMemo(() => {
      const truthy = (v) => v === true || v === 1 || (typeof v === "string" && v.toLowerCase() === "true");

      const getIsPropFunded = (a) => {
        const tagVal = String(a?.tag || a?.settings?.tag || "").toLowerCase();
        return truthy(a?.settings?.isPropFunded) || truthy(a?.isPropFunded) || tagVal === "prop funded";
      };

      const getIsPropEval = (a) =>
        truthy(a?.settings?.isPropFirm) ||
        truthy(a?.settings?.isProp) ||
        truthy(a?.isPropFirm) ||
        truthy(a?.isProp);

      return liveAccounts.map((account) => {
         const funded = getIsPropFunded(account);
         const evalOnly = !funded && getIsPropEval(account);
         const backtest = isBacktesting(account);
         let typeLabel = account.type;
         
         // Strict prioritization prevents duplicate tags
         if (backtest) typeLabel = `${typeLabel} - Backtesting`;
         else if (funded) typeLabel = `${typeLabel} - Prop Funded`;
         else if (evalOnly) typeLabel = `${typeLabel} - Prop Eval`;
         else typeLabel = `${typeLabel} - Retail`;
         
         return { ...account, type: typeLabel, _updatedAt: Date.now() };
      });    
  }, [liveAccounts]);

  // --- Handlers ---
  const handleUpdateAccountName = async (newName) => {
    if (!selectedAccount) return;
    setSaving(true);
    try {
      const accountRef = doc(db, "users", user.uid, "accounts", selectedAccount.id);
      await updateDoc(accountRef, { name: newName });
      setShowEditModal(false);
      window.location.reload();
    } catch {
      alert("Failed to update account name.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!selectedAccount) return;
    setDeleting(true);
    try {
      await deleteAccount(user.uid, selectedAccount.id);
      setShowConfirmDelete(false);
      window.location.reload();
    } catch {
      alert("Failed to delete account.");
    } finally {
      setDeleting(false);
    }
  };

  // --- Render ---
  return (
    <div className="max-w-4xl mx-auto px-2 sm:px-4 pt-8 pb-24 bg-gray-50 dark:bg-gray-900">

      {/* Add Account + Title */}
      <div className="flex flex-wrap justify-between items-center mb-10 gap-2">
        <h1 id="dashboard-header" className="text-2xl font-extrabold text-gray-800 dark:text-white tracking-tight">Dashboard</h1>
        <button
          onClick={onAddAccount}
          className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-400 text-white font-bold px-5 py-2 rounded-2xl shadow hover:from-blue-700 hover:to-blue-500 transition"
        >
          <FaPlus /> Add Account
        </button>
      </div>

      {isSGTSubscriber && (
        <div className="w-full max-w-2xl mx-auto mb-8">
          <div className="bg-gradient-to-r from-fuchsia-100 via-fuchsia-200 to-fuchsia-100 dark:from-fuchsia-900 dark:to-fuchsia-700 rounded-2xl shadow-lg px-8 py-6 border-l-8 border-fuchsia-400 dark:border-fuchsia-600">
            <div className="text-lg italic font-medium text-gray-900 dark:text-gray-100">
              We are 6 figures a day traders and prosperous in every area of our lives! 
            </div>
            <div className="text-md  mt-4 font-medium text-gray-900 dark:text-gray-100 mb-4 text-right pr-20 md:pr-20">
              — SGT
            </div>
          </div>
        </div>
      )}


      {/* Account Selector Dropdown */}
       {liveAccounts.length > 0 && (
          <div className="mb-6">
            <AccountSelector
              data-testid="account-selector-dropdown"
              accounts={accountsForSelector}
              selectedAccountId={selectedAccountId}
              onSelectAccount={setSelectedAccountId}
              isPrivacyMode={isPrivacyMode}
            />
          </div>
        )}

      {/* Main Selected Account Card */}
      {selectedAccount && (
        <div className="w-full max-w-2xl mx-auto">
          <div
            className="
              relative
              bg-white dark:bg-gray-900
              rounded-2xl shadow-lg
              border border-gray-100 dark:border-gray-700
              px-6 sm:px-8 py-6
              pr-20 sm:pr-24
            "
          >
            <div className="flex items-center gap-4 sm:gap-8">
              {/* Left: name + badge */}
              <div className="flex-1 min-w-0">
                <div
                  className="text-lg font-bold text-gray-700 dark:text-gray-200 truncate"
                  title={selectedAccount.name}
                >
                  {selectedAccount.name}
                </div>
                <div className="mt-1">
                  {typeBadge(selectedAccount.type, isPropEval, isPropFunded, isBacktesting(selectedAccount))} {copyBadge(copyRole)}
                </div>
              </div>

              {/* Right: stats */}
              <div className="shrink-0 flex gap-6 sm:gap-8 text-right">
                <div>
                  <div className="text-xs text-gray-500 uppercase mb-1 justify-center">Balance</div>
                  <div className="text-lg font-bold text-blue-700 dark:text-blue-400">
                      {isPrivacyMode ? "******" : Number(balance).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                    </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 uppercase mb-1 justify-center">Win %</div>
                  <div className="text-lg font-bold text-green-600 dark:text-green-400">
                    {winRate}%
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 uppercase mb-1 justify-center">Avg RR</div>
                  <div className="text-lg font-bold text-yellow-600 dark:text-yellow-300">
                    {formatRewardToRisk(avgRewardToRisk)}
                  </div>
                </div>
              </div>
            </div>


            {/* Action buttons container */}
            <div className="absolute bottom-4 right-4 flex flex-col gap-2" style={{ zIndex: 10 }}>
              <button
                className="p-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg transition flex items-center"
                title="Edit Account Name"
                onClick={() => setShowEditModal(true)}
              >
                <FaPencilAlt size={16} />
              </button>
              <button
                className="p-2 rounded-full bg-red-600 hover:bg-red-700 text-white shadow-lg transition flex items-center"
                title="Delete Account"
                onClick={() => setShowConfirmDelete(true)}
              >
                <FaTrash size={16} />
              </button>
            </div>
          </div>

          {/* --- Insight of the Day Card --- */}
      <div className="w-full max-w-2xl mx-auto mb-8 mt-8">
        <div className="bg-blue-500 dark:bg-blue-900 rounded-xl shadow-xl border-l-8 border-blue-500 p-6 relative overflow-hidden">
          
          {/* Content Container */}
          <div className="relative z-10 flex flex-col h-full justify-between">
            
          {/* Header + Icon */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                 <div className="bg-black/20 p-2 rounded-lg">
                    <FaLightbulb className="text-yellow-300 text-lg" />
                 </div>
                 <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-blue-200 opacity-80">
                        {dailyInsight?.type === "wisdom" 
                        ? "Daily Trading Wisdom" 
                        : `Analysis of ${(() => {
                            // Format YYYY-MM-DD to M/D/YYYY safely
                            if (!dailyInsight?.analyzedDate) return "Last Session";
                            const [y, m, d] = dailyInsight.analyzedDate.split('-');
                            return `${Number(m)}/${Number(d)}/${y}`;
                          })()}`
                        }
                    </h3>
                    {/* Date tag removed as requested */}
                 </div>
              </div>
            </div>

            {/* Loading State */}
            {loadingInsight ? (
              <div className="animate-pulse space-y-3 py-2 pl-1">
                <div className="h-6 bg-blue-700 rounded w-3/4"></div>
                <div className="h-4 bg-blue-700 rounded w-full"></div>
                <div className="h-4 bg-blue-700 rounded w-5/6"></div>
              </div>
            ) : dailyInsight ? (
              <>
                {/* Headline */}
                <h2 className="text-xl md:text-2xl font-semibold text-white mb-3 italic tracking-wide">
                  {dailyInsight.headline}
                </h2>
                
                {/* Main Body */}
                <p className="text-blue-100 text-sm leading-relaxed mb-6 font-medium">
                  {dailyInsight.content}
                </p>
                
                {/* Action Item */}
                <div className="bg-black/20 rounded-lg p-3 border border-blue-400/20 flex gap-3 items-start">
                   <div className="shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-yellow-400 shadow-[0_0_8px_rgba(250,204,21,0.6)]"></div>
                   <div className="text-sm">
                      <span className="font-bold text-blue-200 block mb-0.5 text-xs uppercase">Action Plan</span>
                      <span className="text-white opacity-90">{dailyInsight.action}</span>
                   </div>
                </div>
              </>
            ) : (
              <div className="text-blue-300 italic text-sm py-4">
                 Gathering insights from your trading data...
              </div>
            )}
            
          </div>
        </div>
      </div>

    
      {/* --- Edge Alert Card --- */}
      {edgeAlert && (
        <div className="w-full max-w-2xl mx-auto mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border-l-4 border-green-500 p-5 flex items-center justify-between transition-all hover:shadow-lg">
            <div className="flex items-center gap-4">
              <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-full">
                <FaChartLine className="text-green-600 dark:text-green-400 text-xl" />
              </div>
              <div>
                {loadingEdge ? (
                  <div className="animate-pulse space-y-2">
                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-32"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-64"></div>
                  </div>
                ) : (
                  <>
                    <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wide">
                      {edgeAlert.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mt-1 font-medium">
                      {edgeAlert.message}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

          {/* Feature Cards */}
          <div className="w-full max-w-2xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl shadow p-8 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 mb-4">
                <FaListAlt className="text-yellow-500" />
                <span className="font-bold text-lg text-gray-800 dark:text-gray-300">Trades</span>
              </div>
              <div className="text-gray-500 dark:text-gray-400 text-sm mb-2">
                See and add your latest trades for this account.
              </div>
              <button
                className="bg-yellow-500 hover:bg-yellow-500 text-gray-500 dark:text-gray-900 px-4 py-2 rounded-lg font-bold shadow"
                onClick={() => navigate("/journal")}
              >
                Open Journal
              </button>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl shadow p-8 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2 mb-4">
                <FaChartLine className="text-blue-500" />
                <span className="font-bold text-lg text-gray-800 dark:text-gray-100">Stats</span>
              </div>
              <div className="text-gray-500 dark:text-gray-400 text-sm mb-2">
                Review win/loss, growth, and risk stats for this account.
              </div>
              <button
                className="bg-blue-500 hover:bg-blue-600 text-gray-300 dark:text-gray-900 px-4 py-2 rounded-lg font-bold shadow"
                onClick={() => navigate("/stats")}
              >
                View Stats
              </button>
            </div>
          </div>


          {/* Quote of the Day card */}
          <div className="mt-10 mb-10">
            <div className="bg-gradient-to-r from-blue-100 via-blue-200 to-blue-100 dark:from-blue-900 dark:to-blue-700 rounded-2xl shadow-lg px-8 py-6 border-l-8 border-blue-400 dark:border-blue-600">
              {quoteOfTheDay ? (
                <>
                  <div className="text-lg italic font-medium text-gray-900 dark:text-gray-100 mb-2">
                    “{quoteOfTheDay.text}”
                  </div>
                  <div className="text-right font-semibold text-gray-800 dark:text-gray-300">
                    — {quoteOfTheDay.author}
                  </div>
                  <div className="text-xs text-gray-400 mt-2">
                    Quote of the Day
                  </div>
                </>
              ) : (
                <div className="text-gray-500 italic">No quote available.</div>
              )}
            </div>
          </div>

          {/* [!code ++] Mount the modal */}
      {showEndOfTrial && selectedAccountId && (
        <EndOfTrialModal 
          userId={user.uid}
          accountId={selectedAccountId}
          onClose={() => setShowEndOfTrial(false)}
        />
      )}



{/* --- Daily Summary Card --- */}
            {showDailySummary && (
              <div className="w-full max-w-2xl mx-auto bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
                
                  {/* CALCULATE AGGREGATES HERE */}
                {(() => {
                  const nonDemoStats = dailyStats.filter(
                    (acc) => !String(acc?.name || "").toLowerCase().includes("demo")
                  );
                  const aggregateDaily = nonDemoStats.reduce(
                    (sum, acc) => sum + (acc.dailyPnL || 0),
                    0
                  );
                  const aggregateWeekly = nonDemoStats.reduce(
                    (sum, acc) => sum + (acc.weeklyPnL || 0),
                    0
                  );
                  
                  return (
                    <>
                      {/* HEADER WITH TOGGLES */}
                      <div className="flex items-center justify-between mb-4 border-b dark:border-gray-700 pb-3">
                        <div className="flex items-center gap-2">
                          <FaTable className="text-green-500" />
                          <span className="font-bold text-lg text-gray-800 dark:text-white">Today's Overview (All Accounts)</span>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          {/* ADMIN TOGGLE */}
                          {isAdmin && (
                            <div className="flex items-center gap-2 border-r dark:border-gray-700 pr-4">
                              <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Totals</span>
                              <button 
                                onClick={() => setShowAdminTotals(!showAdminTotals)}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${showAdminTotals ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                              >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showAdminTotals ? 'translate-x-4' : 'translate-x-1'}`} />
                              </button>
                            </div>
                          )}

                          <button 
                            onClick={() => setHideValues(!hideValues)}
                            disabled={isPrivacyMode}
                            className={`p-2 transition ${isPrivacyMode ? "text-gray-300 dark:text-gray-600 cursor-not-allowed" : "text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"}`}
                            title={effectiveHideValues ? "Show Values" : "Hide Values"}
                          >
                            {effectiveHideValues ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                          </button>
                        </div>
                      </div>
                      
                      {loadingDaily ? (
                        <div className="text-center text-gray-500 py-4">Loading daily stats...</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm text-left">
                            <thead>
                              <tr className="text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                                <th className="py-2 px-2">Account</th>
                                <th className="py-2 px-2 text-right">Balance</th>
                                <th className="py-2 px-2 text-right">Effective Balance</th>
                                <th className="py-2 px-2 text-right">Today's P&L</th>
                                <th className="py-2 px-2 text-right">Today %</th>
                                <th className="py-2 px-2 text-right">Today R:R</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dailyStats.map((acc, index) => (
                                <tr key={`${acc.name}-${index}`} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                  <td className="py-3 px-2 font-medium text-gray-800 dark:text-gray-200">
                                    <div className="flex items-center">
                                      {acc.role === "leader" && (
                                        <span title="Leader" className="mr-2 flex items-center justify-center w-5 h-5 rounded bg-yellow-500 text-white text-xs font-bold shrink-0">L</span>
                                      )}
                                      {acc.role === "follower" && (
                                        <span title="Follower" className="mr-2 flex items-center justify-center w-5 h-5 rounded bg-purple-600 text-white text-xs font-bold shrink-0">F</span>
                                      )}
                                      <span className="truncate">{acc.name}</span>
                                    </div>
                                  </td>
                                  <td className="py-3 px-2 text-right text-gray-600 dark:text-gray-300">
                                    {effectiveHideValues ? "****" : Number(acc.balance).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                  </td>
                                  <td className="py-3 px-2 text-right text-gray-600 dark:text-gray-300 font-semibold">
                                    {effectiveHideValues ? "****" : Number(acc.effectiveBalance).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                  </td>
                                  <td className={`py-3 px-2 text-right font-semibold ${
                                    effectiveHideValues || acc.dailyPnL === 0 ? 'text-gray-400 dark:text-gray-500' : acc.dailyPnL > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                                  }`}>
                                    {effectiveHideValues ? "****" : <>{acc.dailyPnL > 0 ? "+" : ""}{Number(acc.dailyPnL).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</>}
                                  </td>
                                  <td className={`py-3 px-2 text-right ${
                                    effectiveHideValues || acc.percentChange === 0 ? 'text-gray-400 dark:text-gray-500' : acc.percentChange > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                                  }`}>
                                    {effectiveHideValues ? "****" : <>{acc.percentChange > 0 ? "+" : ""}{acc.percentChange.toFixed(2)}%</>}
                                  </td>
                                  <td className="py-3 px-2 text-right text-yellow-600 dark:text-yellow-400">
                                    {effectiveHideValues ? "****" : (acc.avgRR ? formatRewardToRisk(acc.avgRR) : "--")}
                                  </td>
                                </tr>
                              ))}
                              {dailyStats.length === 0 && (
                                <tr>
                                  <td colSpan="6" className="py-4 text-center text-gray-500">No accounts found.</td>
                                </tr>
                              )}
                            </tbody>
                            
                            {/* NEW ADMIN FOOTER ROW */}
                            {isAdmin && showAdminTotals && dailyStats.length > 0 && (
                              <tfoot className="bg-gray-50 dark:bg-gray-800/80 border-t-2 border-gray-200 dark:border-gray-700">
                                <tr>
                                  <td colSpan="3" className="py-3 px-2 font-bold text-right text-gray-800 dark:text-gray-200 uppercase tracking-wider text-xs">
                                    Aggregate P&L (All Accounts):
                                  </td>
                                  <td className={`py-3 px-2 text-right font-bold ${effectiveHideValues ? 'text-gray-400' : aggregateDaily >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                    <div className="text-[10px] text-gray-500 font-normal uppercase mb-0.5">Today</div>
                                    {effectiveHideValues ? "****" : (aggregateDaily >= 0 ? "+" : "") + Number(aggregateDaily).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                  </td>
                                  <td colSpan="2" className={`py-3 px-2 text-left font-bold ${effectiveHideValues ? 'text-gray-400' : aggregateWeekly >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                    <div className="text-[10px] text-gray-500 font-normal uppercase mb-0.5">This Week</div>
                                    {effectiveHideValues ? "****" : (aggregateWeekly >= 0 ? "+" : "") + Number(aggregateWeekly).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                  </td>
                                </tr>
                              </tfoot>
                            )}
                          </table>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

          </div>
        )}

      {/* Edit Account Modal */}
      <EditAccountModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSave={handleUpdateAccountName}
        currentName={selectedAccount ? selectedAccount.name : ""}
        loading={saving}
      />

      {/* Confirmation Delete Modal */}
      <ConfirmDeleteModal
        open={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        onConfirm={handleDeleteAccount}
        accountName={selectedAccount ? selectedAccount.name : ""}
        loading={deleting}
      />

      {/* NEW: What's New Modal */}
      <WhatsNewModal 
        open={showWhatsNew} 
        onClose={handleCloseWhatsNew} 
      />

      {/* Strategy Reminder Modal */}
            <StrategyReminderModal
              open={showStrategyReminder}
              onClose={handleCloseStrategyReminder}
              onNavigate={() => {
                handleCloseStrategyReminder();
                navigate("/lessons");
              }}
            />


      {/* Empty state */}
      {accounts.length === 0 && (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-12 text-center shadow-xl mt-16">
          <p className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-3">
            No accounts yet!
          </p>
          <button
            onClick={onAddAccount}
            className="mt-3 px-6 py-2 bg-blue-500 text-white rounded-xl shadow hover:bg-blue-700 font-bold"
          >
            <FaPlus className="inline mr-2" />
            Create your first account
          </button>
        </div>
      )}

       {/* Floating Calculator */}
      <button onClick={() => setShowCalculator(true)} className="fixed bottom-12 right-6 z-40 bg-gray-800 text-white p-4 rounded-full shadow-lg hover:scale-110 transition">
        <FaCalculator />
      </button>
      {showCalculator && <SimpleCalculator onClose={() => setShowCalculator(false)} />}
    </div>
  );
}